import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useWorkspaces } from '@/hooks/useWorkspaces';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { CreateWorkspaceDirectModal } from '@/components/CreateWorkspaceDirectModal';
import { Loader2, Building, Plus } from 'lucide-react';

export const SelectWorkspace = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { workspaces, isLoading } = useWorkspaces();
  const [showCreateModal, setShowCreateModal] = useState(false);

  useEffect(() => {
    if (!user) {
      navigate('/auth');
      return;
    }

    // Auto-redirect if user has only one workspace
    if (!isLoading && workspaces.length === 1) {
      navigate(`/w/${workspaces[0].id}/dashboard`);
    }
  }, [user, workspaces, isLoading, navigate]);

  const handleWorkspaceSelect = (workspaceId: string) => {
    navigate(`/w/${workspaceId}/dashboard`);
  };

  const handleWorkspaceCreated = (workspaceId: string) => {
    setShowCreateModal(false);
    navigate(`/w/${workspaceId}/dashboard`);
  };

  if (!user) {
    return null;
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex items-center space-x-2">
          <Loader2 className="h-6 w-6 animate-spin" />
          <span className="text-lg">Loading workspaces...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-md space-y-6">
        <div className="text-center">
          <h1 className="text-3xl font-bold">Select Workspace</h1>
          <p className="text-muted-foreground mt-2">
            Choose a workspace to continue
          </p>
        </div>

        <div className="space-y-4">
          {workspaces.map((workspace) => (
            <Card key={workspace.id} className="cursor-pointer hover:shadow-md transition-shadow">
              <CardContent className="p-4">
                <Button
                  variant="ghost"
                  className="w-full h-auto p-0 justify-start"
                  onClick={() => handleWorkspaceSelect(workspace.id)}
                >
                  <div className="flex items-center space-x-3">
                    <Building className="h-8 w-8 text-primary" />
                    <div className="text-left">
                      <h3 className="font-semibold">{workspace.name}</h3>
                      <p className="text-sm text-muted-foreground">
                        {workspace.organization?.name || 'Organization'}
                      </p>
                    </div>
                  </div>
                </Button>
              </CardContent>
            </Card>
          ))}

          <Card className="border-dashed">
            <CardContent className="p-4">
              <Button
                variant="ghost"
                className="w-full h-auto p-0 justify-center"
                onClick={() => setShowCreateModal(true)}
              >
                <div className="flex items-center space-x-3 text-center">
                  <Plus className="h-8 w-8 text-muted-foreground" />
                  <div>
                    <h3 className="font-semibold">Create New Workspace</h3>
                    <p className="text-sm text-muted-foreground">
                      Start fresh with a new workspace
                    </p>
                  </div>
                </div>
              </Button>
            </CardContent>
          </Card>
        </div>

        <CreateWorkspaceDirectModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onSuccess={handleWorkspaceCreated}
        />
      </div>
    </div>
  );
};